/**
 * @fileoverview Merge de listado de chatbots: API Mercately y/o chatbots MS (microservicio de chatbots).
 * Se piden `per_page` chatbots de cada fuente usando `offset` como cursor, se mezclan por created_at
 * desc y se muestran `per_page`; el cursor de cada fuente avanza según cuántos entraron en la página.
 * `total_pages` se calcula con los totales reales (`total`) reportados por ambas APIs.
 *
 * @module dualApiMergeChatbotService
 */
import { getJwtCookie } from '../../util/jwtCookie';

export function normalizeMsChatbotsBaseUrl() {
  const raw = typeof ENV !== 'undefined' && ENV?.MS_CHATBOTS_API_URL != null
    ? String(ENV.MS_CHATBOTS_API_URL).trim()
    : '';
  return raw.replace(/\/+$/, '');
}

export function assertMsChatbotsUrlConfigured() {
  return normalizeMsChatbotsBaseUrl() || 'http://localhost:3004/chatbots/api/v1';
}

export function msChatbotsApiUrl(suffixPath) {
  const base = assertMsChatbotsUrlConfigured();
  const suffix = suffixPath.startsWith('/') ? suffixPath : `/${suffixPath}`;
  return `${base}${suffix}`;
}

export async function buildMsChatbotsAuthHeaders() {
  const token = await getJwtCookie();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getChatbotDate(chatbot) {
  const dateStr = chatbot.created_at || chatbot.attributes?.created_at || 0;
  return new Date(dateStr).getTime();
}

function calculateOffsets(page, previousPagination) {
  const isFirstPage = page === 1;
  return {
    mercatelyOffset: isFirstPage ? 0 : (previousPagination?.mercatelyOffset || 0),
    microserviceOffset: isFirstPage ? 0 : (previousPagination?.microserviceOffset || 0)
  };
}

function getRealTotal(response) {
  return Number.isFinite(response?.total) ? response.total : null;
}

function calculateTotalPages({
  currentPage,
  perPage,
  mcResponse,
  msResponse,
  hasUnusedMercately,
  hasUnusedMicroservice
}) {
  const mcTotal = getRealTotal(mcResponse);
  const msTotal = getRealTotal(msResponse);

  if (mcTotal !== null && msTotal !== null) {
    // La lista mostrada es la unión de ambas fuentes: total de páginas real.
    return Math.max(currentPage, Math.ceil((mcTotal + msTotal) / perPage));
  }

  // Fallback mientras las APIs no expongan `total`: se conserva el comportamiento previo
  // (página actual + 1 mientras haya siguiente) para no mostrar páginas vacías.
  const hasNext = currentPage < (mcResponse?.total_pages || 1)
    || currentPage < (msResponse?.total_pages || 1)
    || hasUnusedMercately
    || hasUnusedMicroservice;

  return hasNext ? currentPage + 1 : currentPage;
}

async function fetchMercatelyAndMicroservice(mercatelyParams, microserviceParams) {
  const mcQueryParams = new URLSearchParams(mercatelyParams).toString();
  const msQueryParams = new URLSearchParams(microserviceParams).toString();

  const mcEndpoint = `/api/v1/chatbots?${mcQueryParams}`;
  const msEndpoint = msChatbotsApiUrl(`/chatbots?${msQueryParams}`);

  const msHeaders = await buildMsChatbotsAuthHeaders();

  return Promise.all([
    fetch(mcEndpoint, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(res => res.json()).catch(err => {
      // eslint-disable-next-line no-console
      console.error('Error fetching Mercately chatbots', err);
      return { total_pages: 0, chatbots: [] };
    }),

    fetch(msEndpoint, {
      method: 'GET',
      headers: msHeaders
    }).then(res => res.json()).catch(err => {
      // eslint-disable-next-line no-console
      console.error('Error fetching Microservice chatbots', err);
      return { total_pages: 0, chatbots: [] };
    })
  ]);
}

export const microserviceChatbotsCache = new Set();

export async function fetchAndMergeDualApiChatbots(params, previousPagination) {
  const perPage = params.per_page || 25;
  const currentPage = params.page || 1;

  const { mercatelyOffset, microserviceOffset } = calculateOffsets(currentPage, previousPagination);

  const mercatelyParams = {
    ...params,
    page: currentPage,
    offset: mercatelyOffset,
    per_page: perPage
  };

  const microserviceParams = {
    ...params,
    page: currentPage,
    offset: microserviceOffset,
    per_page: perPage
  };

  const [mcResponse, msResponse] = await fetchMercatelyAndMicroservice(mercatelyParams, microserviceParams);

  const mcRaw = mcResponse?.chatbots || [];
  const msRaw = msResponse?.chatbots || [];

  const mcChatbots = mcRaw.map(bot => ({ ...bot, isFromMicroservice: false }));
  const msChatbots = msRaw.map(bot => {
    microserviceChatbotsCache.add(bot.web_id);
    return { ...bot, isFromMicroservice: true };
  });

  // Mezclar y ordenar por created_at DESC (más nuevos primero)
  const combined = [...mcChatbots, ...msChatbots].sort((a, b) => {
    const dateA = getChatbotDate(a);
    const dateB = getChatbotDate(b);
    return dateB - dateA;
  });

  // La página muestra exactamente `perPage` filas; el cursor de cada fuente avanza
  // solo por los chatbots suyos que entraron en la página.
  const topChatbots = combined.slice(0, perPage);

  let usedMercately = 0;
  let usedMicroservice = 0;

  topChatbots.forEach(bot => {
    if (bot.isFromMicroservice) {
      usedMicroservice += 1;
    } else {
      usedMercately += 1;
    }
  });

  const hasUnusedMercately = usedMercately < mcChatbots.length;
  const hasUnusedMicroservice = usedMicroservice < msChatbots.length;

  const totalPages = calculateTotalPages({
    currentPage,
    perPage,
    mcResponse,
    msResponse,
    hasUnusedMercately,
    hasUnusedMicroservice
  });

  const hasNextPage = currentPage < totalPages || hasUnusedMercately || hasUnusedMicroservice;

  const pagination = {
    current_page: currentPage,
    per_page: perPage,
    has_next_page: hasNextPage,
    has_previous_page: currentPage > 1,
    mercatelyOffset: mercatelyOffset + usedMercately,
    microserviceOffset: microserviceOffset + usedMicroservice,
    total_pages: totalPages
  };

  return { chatbots: topChatbots, total_pages: totalPages, pagination };
}

export async function getDualApiChatbot(webId) {
  // 1. Intentar primero con el microservicio de chatbots
  try {
    const msEndpoint = msChatbotsApiUrl(`/chatbots/${webId}`);
    const msHeaders = await buildMsChatbotsAuthHeaders();
    const res = await fetch(msEndpoint, { headers: msHeaders });
    if (res.ok) {
      const data = await res.json();
      if (data?.chatbot) {
        microserviceChatbotsCache.add(webId);
        return {
          chatbot: { ...data.chatbot, isFromMicroservice: true }
        };
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching chatbot from microservice, falling back to Mercately', err);
  }

  // 2. Fallback al monolito de Mercately
  const mcEndpoint = `/api/v1/chatbots/${webId}`;
  const res = await fetch(mcEndpoint, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });
  if (!res.ok) throw new Error('Flujo no encontrado');
  const data = await res.json();
  return {
    chatbot: { ...data.chatbot, isFromMicroservice: false }
  };
}

export function clearMicroserviceChatbotsCache() {
  microserviceChatbotsCache.clear();
}

